import { useCallback, useEffect, useRef, useState } from "react";
import { publicApi } from "./orderApi";
import { deviceId } from "./useCart";
import { accountKey } from "./razorpay";

const NAME_KEY = "sc_online_name";
const PHONE_KEY = "sc_online_phone";

/** 30-day customer session: token stored locally, ALWAYS validated server-side (GET /account/session). */
export const useSession = (slug) => {
  const [state, setState] = useState({ status: "checking", token: localStorage.getItem(accountKey(slug)) || "", phone: "", name: localStorage.getItem(NAME_KEY) || "" });

  const clearLocal = useCallback(() => {
    localStorage.removeItem(accountKey(slug));
    setState((s) => ({ ...s, status: "none", token: "", phone: "" }));
  }, [slug]);

  const [recheck, setRecheck] = useState(0);
  const statusRef = useRef(state.status); statusRef.current = state.status;
  useEffect(() => {
    const token = localStorage.getItem(accountKey(slug)) || "";
    if (!token) { setState((s) => ({ ...s, status: "none", token: "", phone: "" })); return; }
    let alive = true;
    publicApi.get(`/${slug}/account/session`, { params: { token, device_id: deviceId() } })
      .then((r) => alive && setState({ status: "valid", token, phone: r.data.phone, name: r.data.customer_name || localStorage.getItem(NAME_KEY) || "", expiresAt: r.data.expires_at }))
      // Only an explicit 401/403 ends the session. A network blip (Safari app switch, flaky mobile data) keeps the
      // token and is re-checked when the page is shown again / comes back online — never a silent logout.
      .catch((e) => { if (!alive) return; if (e.response?.status === 401 || e.response?.status === 403) clearLocal(); else setState((s) => ({ ...s, status: "offline", token })); });
    return () => { alive = false; };
  }, [slug, clearLocal, recheck]);
  useEffect(() => {
    const again = () => { if (document.visibilityState === "hidden") return; if (localStorage.getItem(accountKey(slug))) setRecheck((n) => n + 1); };
    const onShow = (e) => { if (e.persisted) again(); };           // Safari back/forward cache restore
    window.addEventListener("online", again); window.addEventListener("pageshow", onShow);
    const onVis = () => { if (document.visibilityState === "visible" && statusRef.current === "offline") again(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("online", again); window.removeEventListener("pageshow", onShow); document.removeEventListener("visibilitychange", onVis); };
  }, [slug]);

  const save = useCallback((token, phone, name) => {
    localStorage.setItem(accountKey(slug), token);
    if (name) localStorage.setItem(NAME_KEY, name);
    if (phone) localStorage.setItem(PHONE_KEY, phone.replace(/^\+91/, ""));
    setState({ status: "valid", token, phone, name: name || "" });
  }, [slug]);

  const logout = useCallback(async () => {
    const token = localStorage.getItem(accountKey(slug));
    if (token) { try { await publicApi.post(`/${slug}/account/logout`, null, { params: { token } }); } catch { /* best effort; local state is cleared anyway */ } }
    clearLocal();
  }, [slug, clearLocal]);

  const updateName = async (n) => {
    const r = await publicApi.put(`/${slug}/account/profile`, { customer_name: n, token: state.token, device_id: deviceId() });
    localStorage.setItem(NAME_KEY, r.data.customer_name);
    setState((s) => ({ ...s, name: r.data.customer_name }));
    return r.data.customer_name;
  };
  return { ...state, valid: state.status === "valid", save, logout, updateName, rememberName: (n) => localStorage.setItem(NAME_KEY, n) };
};

export const localPhone = () => localStorage.getItem(PHONE_KEY) || "";
export const displayPhone = (p) => { const d = (p || "").replace(/\D/g, "").slice(-10); return d ? `+91 ${d.slice(0, 2)}••••••${d.slice(-2)}` : ""; };
